import { useEffect, useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../utils/supabase'
import { createNadDebug } from '../utils/debugLog'
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_RULES,
  getPasswordRuleTranslationKey,
  translatePasswordError,
  validatePassword,
} from '../utils/passwordPolicy'
import Button from '../components/ui/Button'
import { classifyAuthError, describeAuthError } from '../components/auth/humanizeAuthError'

const log = createNadDebug('dw:auth-password-policy:reset')
// Raw-error trace for the "unexpected → contact the dreamwrite team" mapping,
// on the same feature tag as the login form (nad trace --feature dw:auth-error-map).
const errLog = createNadDebug('dw:auth-error-map')

/**
 * Parse recovery params from BOTH the URL fragment and the query string.
 *
 * New (scanner-safe) recovery links carry a one-time `token_hash` in the
 * fragment: `/reset-password#token_hash=...&type=recovery`. The fragment never
 * reaches the HTTP server, so it stays out of access logs / referrers, and the
 * token is only spent when this page calls `verifyOtp` on explicit user submit
 * — a passive mail-scanner GET of this page consumes nothing.
 *
 * GoTrue's legacy `/verify` failure redirects append `error` / `error_code` /
 * `error_description` (in the fragment or query). Legacy *success* recovery
 * links use `#access_token=...&type=recovery`, which supabase-js consumes on its
 * own and surfaces via the `PASSWORD_RECOVERY` auth event (handled below).
 */
function parseRecoveryParams() {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash
  const hashParams = new URLSearchParams(hash)
  const queryParams = new URLSearchParams(window.location.search)

  const pick = (key: string) => hashParams.get(key) ?? queryParams.get(key)

  return {
    tokenHash: pick('token_hash'),
    type: pick('type'),
    // Legacy implicit-flow recovery session arriving in the fragment.
    accessToken: hashParams.get('access_token'),
    hasAccessToken: hashParams.has('access_token'),
    errorCode: pick('error_code'),
    errorDescription: pick('error_description'),
    error: pick('error'),
    fromFragment: hashParams.has('token_hash'),
    hasQueryToken: queryParams.has('token_hash'),
  }
}

type LinkError = { code: string | null; description: string }

// How long to wait for a legacy implicit-flow `PASSWORD_RECOVERY` event before
// giving up and showing the definite error state (instead of an infinite spinner).
const LEGACY_RECOVERY_TIMEOUT_MS = 8000

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('auth')
  const [tokenHash, setTokenHash] = useState<string | null>(null)
  // Authorization granted ONLY by a fresh verifyOtp(recovery) this session, or
  // a PASSWORD_RECOVERY auth event (legacy implicit-flow link). An ambient
  // logged-in session must NEVER authorize a password change here.
  const [recoveryAuthorized, setRecoveryAuthorized] = useState(false)
  const [legacyPending, setLegacyPending] = useState(false)
  const [linkError, setLinkError] = useState<LinkError | null>(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const parsed = parseRecoveryParams()
    log({
      event: 'reset-link-parsed',
      hasTokenHash: Boolean(parsed.tokenHash),
      hasQueryToken: parsed.hasQueryToken,
      hasAccessToken: parsed.hasAccessToken,
      hasErrorParam: Boolean(parsed.error || parsed.errorCode),
      source: parsed.tokenHash ? (parsed.fromFragment ? 'fragment' : 'query') : 'none',
    })

    if (parsed.tokenHash && parsed.type === 'recovery') {
      setTokenHash(parsed.tokenHash)
    } else if (parsed.tokenHash) {
      // A token_hash for a non-recovery flow (confirmation/invite/email-change)
      // or with no type must NOT be submitted to the recovery verifier.
      setLinkError({
        code: parsed.type ? `unexpected_type:${parsed.type}` : 'missing_type',
        description: t('resetLinkInvalid'),
      })
    } else if (parsed.error || parsed.errorCode) {
      // Classify the legacy GoTrue redirect error (error / error_code /
      // error_description). A recognized expired/invalid recovery link keeps its
      // actionable copy; anything unexpected shows the support message instead of
      // rendering a raw provider `error_description` verbatim.
      const paramError = {
        code: parsed.errorCode ?? parsed.error,
        message: parsed.errorDescription ?? parsed.error ?? '',
      }
      const kind = classifyAuthError(paramError, 'recovery-verify')
      errLog({
        event: 'reset-link-param-error',
        trace_id: 'auth-error-map',
        code: paramError.code,
        message: paramError.message,
        kind,
      })
      setLinkError({
        code: parsed.errorCode ?? parsed.error,
        // Expired/invalid link keeps localized copy; a readable rate limit (etc.)
        // is preserved verbatim; anything unexpected → contact-team.
        description:
          kind === 'recovery_link_invalid'
            ? t('resetLinkInvalidOrExpired')
            : describeAuthError(t, paramError, 'recovery-verify'),
      })
    } else if (parsed.hasAccessToken && parsed.type === 'recovery') {
      // Legacy implicit-flow recovery link — supabase-js consumes the fragment
      // and emits PASSWORD_RECOVERY. That event can fire BEFORE our listener
      // below subscribes (the global AuthProvider also calls getSession/
      // onAuthStateChange), and a late subscriber only gets INITIAL_SESSION,
      // never a replayed PASSWORD_RECOVERY. So also check getSession() — but
      // ONLY accept the session that was actually created FROM this fragment's
      // access_token (access_token match), never a pre-existing ambient session.
      // This blocks a logged-in user opening a crafted `#access_token=…&
      // type=recovery` URL from being authorized off their ambient login.
      setLegacyPending(true)
      const fragmentAccessToken = parsed.accessToken
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session && fragmentAccessToken && session.access_token === fragmentAccessToken) {
          log({ event: 'reset-authorized', via: 'legacy_recovery_session' })
          setRecoveryAuthorized(true)
          setLegacyPending(false)
        }
      })
    }

    // Strip the token / error params from the URL so the one-time token does
    // not linger in history, copy-paste, or referrers. Only strip when WE
    // parsed something other than a legacy access_token fragment (which
    // supabase-js still needs to consume for the legacy event path).
    if (parsed.tokenHash || parsed.error || parsed.errorCode) {
      window.history.replaceState(null, '', window.location.pathname)
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        log({ event: 'reset-authorized', via: 'password_recovery_event' })
        setRecoveryAuthorized(true)
        setLegacyPending(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [t])

  // Bound the legacy "Validating…" state so a stale/expired implicit link does
  // not hang forever — fall back to the definite error state.
  useEffect(() => {
    if (!legacyPending) return
    const timer = setTimeout(() => {
      if (!recoveryAuthorized) {
        setLegacyPending(false)
        setLinkError({
          code: 'recovery_link_expired',
          description: t('resetLinkInvalidOrUsed'),
        })
      }
    }, LEGACY_RECOVERY_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [legacyPending, recoveryAuthorized, t])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const validation = validatePassword(password)
    if (!validation.ok) {
      log('reject failedRules=%o len=%d', validation.failedRuleIds, password.length)
      setError(translatePasswordError(t, validation))
      return
    }
    if (password !== confirm) {
      setError(t('resetPasswordsDoNotMatch'))
      return
    }

    setLoading(true)

    // Authorize the password change. The ONLY valid sources are a fresh
    // verifyOtp(recovery) exchange or an already-received PASSWORD_RECOVERY
    // event. Spending the one-time token here (explicit user action) is what
    // makes the flow safe against passive mail-scanner prefetches.
    let authorized = recoveryAuthorized
    if (!authorized) {
      if (tokenHash) {
        log({ event: 'recovery-verify-start' })
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type: 'recovery',
          token_hash: tokenHash,
        })
        if (verifyError) {
          // Log the specific reason faithfully (status + code + message) under
          // the dw:auth-error-map tag, then drop to the definite error state. A
          // recognized expired/invalid token keeps its actionable "request a
          // fresh link" copy; an unexpected failure (5xx / network / unknown)
          // shows the support message instead of a raw GoTrue string.
          const kind = classifyAuthError(verifyError, 'recovery-verify')
          errLog({
            event: 'recovery-verify-fail',
            trace_id: 'auth-error-map',
            code: verifyError.code ?? null,
            status: verifyError.status ?? null,
            message: verifyError.message,
            kind,
          })
          setTokenHash(null)
          setLinkError({
            code: verifyError.code ?? 'recovery_verify_failed',
            // Expired/invalid link keeps localized copy; a readable rate limit
            // (etc.) is preserved verbatim; anything unexpected → contact-team.
            description:
              kind === 'recovery_link_invalid'
                ? t('resetLinkInvalidOrExpired')
                : describeAuthError(t, verifyError, 'recovery-verify'),
          })
          setLoading(false)
          return
        }
        log({ event: 'recovery-verify-ok' })
        log({ event: 'reset-authorized', via: 'verifyOtp' })
        authorized = true
        setRecoveryAuthorized(true)
      } else {
        // Regression sentinel: an ambient session (no recovery token) must
        // never reach updateUser. The render gate should already prevent this.
        log({ event: 'reset-blocked-no-recovery' })
        setLoading(false)
        setLinkError({
          code: 'no_recovery_token',
          description: t('resetLinkInvalidOrUsed'),
        })
        return
      }
    }

    const { error: updateError } = await supabase.auth.updateUser({ password })
    setLoading(false)

    if (updateError) {
      // weak_password / same_password keep their specific copy; anything
      // unexpected → contact-team (never render the raw provider message).
      errLog({
        event: 'reset-update-fail',
        trace_id: 'auth-error-map',
        code: updateError.code ?? null,
        status: updateError.status ?? null,
        message: updateError.message,
      })
      setError(describeAuthError(t, updateError, 'reset-update'))
      return
    }
    log({ event: 'reset-updated' })
    navigate('/', { replace: true })
  }

  const showForm = Boolean(tokenHash) || recoveryAuthorized

  if (!showForm) {
    if (legacyPending) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface">
          <div className="card p-8 max-w-md w-full text-center">
            <p className="text-text-secondary">{t('validatingResetLink')}</p>
          </div>
        </div>
      )
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div
          className="card p-8 max-w-md w-full text-center space-y-4"
          data-testid="reset-link-error"
        >
          <h1 className="text-2xl font-bold">{t('resetLinkUnavailable')}</h1>
          <p className="text-text-secondary text-sm">
            {linkError?.description ?? t('resetLinkInvalidOrUsed')}
          </p>
          <p className="text-text-muted text-xs font-medium">
            {t('resetLinkSingleUse')}
          </p>
          <Button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className="w-full"
          >
            {t('backToSignIn')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <form onSubmit={handleSubmit} className="card p-8 max-w-md w-full space-y-4">
        <h1 className="text-2xl font-bold text-center">{t('setNewPassword')}</h1>

        <div>
          <label htmlFor="new-password" className="block text-sm text-text-secondary mb-1">
            {t('newPassword')}
          </label>
          <input
            id="new-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('passwordRuleLength', { count: MIN_PASSWORD_LENGTH })}
            className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
            minLength={MIN_PASSWORD_LENGTH}
            required
            disabled={loading}
          />
          <ul className="mt-2 space-y-0.5" data-testid="password-requirements">
            {PASSWORD_RULES.map((rule) => {
              const met = rule.test(password)
              return (
                <li
                  key={rule.id}
                  data-testid={`password-rule-${rule.id}`}
                  data-met={met}
                  className={`text-xs font-medium flex items-center gap-1.5 ${met ? 'text-green-400' : 'text-text-muted'}`}
                >
                  <span aria-hidden>{met ? '✓' : '○'}</span>
                  {rule.id === 'length' ? t(getPasswordRuleTranslationKey(rule.id), { count: MIN_PASSWORD_LENGTH }) : t(getPasswordRuleTranslationKey(rule.id))}
                </li>
              )
            })}
          </ul>
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm text-text-secondary mb-1">
            {t('confirmNewPassword')}
          </label>
          <input
            id="confirm-password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={t('reenterNewPassword')}
            className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
            minLength={MIN_PASSWORD_LENGTH}
            required
            disabled={loading}
          />
        </div>

        {error && (
          <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <Button
          type="submit"
          loading={loading}
          disabled={loading || !validatePassword(password).ok || confirm !== password}
          className="w-full"
        >
          {loading ? t('updating') : t('updatePassword')}
        </Button>
      </form>
    </div>
  )
}
